import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Flag, Loader2, Trash2, CheckCircle2, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const mediaUrl = (url) => (url && url.startsWith('/') ? `${API}${url}` : url);

const REASON_LABELS = {
  spam: { en: 'Spam', es: 'Spam' },
  inappropriate: { en: 'Inappropriate', es: 'Inapropiado' },
  harassment: { en: 'Harassment', es: 'Acoso' },
  other: { en: 'Other', es: 'Otro' },
};

const ReportsAdminPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/reports?status=open`, cfg);
      setReports(data.reports || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudieron cargar los reportes' : 'Could not load reports'));
      setReports([]);
    } finally { setLoading(false); }
  }, [es]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (report, action) => {
    setBusyId(report.id);
    try {
      await axios.post(`${API}/api/admin/reports/${report.id}/resolve`, { action }, cfg);
      toast.success(action === 'remove'
        ? (es ? 'Publicación eliminada' : 'Post removed')
        : (es ? 'Reporte descartado' : 'Report dismissed'));
      setReports((r) => r.filter((x) => x.id !== report.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'Error' : 'Something went wrong'));
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="reports-empty">
        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
        <p>{es ? 'No hay reportes pendientes. ¡Todo limpio!' : 'No open reports. All clear!'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="reports-panel">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ShieldAlert className="w-4 h-4" />
        {reports.length} {es ? 'reporte(s) pendiente(s)' : 'open report(s)'}
      </div>
      {reports.map((r) => (
        <Card key={r.id} data-testid={`report-row-${r.id}`}>
          <CardContent className="p-3 flex gap-3">
            {r.post?.image_url ? (
              <img src={mediaUrl(r.post.image_url)} alt="" className="w-20 h-20 rounded-lg object-cover bg-muted shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-muted shrink-0 flex items-center justify-center text-muted-foreground"><Flag className="w-5 h-5" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="destructive" className="capitalize">{(REASON_LABELS[r.reason] || {})[es ? 'es' : 'en'] || r.reason}</Badge>
                {r.post?.is_deleted && <Badge variant="outline">{es ? 'Ya eliminada' : 'Already removed'}</Badge>}
                <span className="text-xs text-muted-foreground">{es ? 'por' : 'by'} {r.reporter_name}</span>
              </div>
              <p className="text-sm mt-1">
                <span className="text-muted-foreground">{es ? 'Autor:' : 'Author:'}</span>{' '}
                <span className="font-medium">{r.author_name}</span>
              </p>
              {r.post?.caption && <p className="text-sm text-muted-foreground truncate">"{r.post.caption}"</p>}
              {r.note && <p className="text-xs italic text-muted-foreground mt-0.5">"{r.note}"</p>}
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => resolve(r, 'dismiss')} disabled={busyId === r.id} data-testid={`report-dismiss-${r.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> {es ? 'Descartar' : 'Dismiss'}
                </Button>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => resolve(r, 'remove')} disabled={busyId === r.id} data-testid={`report-remove-${r.id}`}>
                  {busyId === r.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} {es ? 'Eliminar publicación' : 'Remove post'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ReportsAdminPanel;
